import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { IRange } from '../../../types';
import { IDateRangeConfigOptions } from './types';

export type SlidingDateRange =
  | 'today'
  | 'last-week'
  | 'last-2-weeks'
  | 'last-month'
  | 'last-3-months'
  | 'last-6-months'
  | 'last-year'
  | 'all-time'
  | 'custom'
  | 'event';

export interface ISlidingDateRangeTypeOption {
  label: string;
  value: SlidingDateRange;
}

/**
 * @description the available sliding date range options
 */
const defaultSlidingOptions: ISlidingDateRangeTypeOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last Week', value: 'last-week' },
  { label: 'Last 2 Weeks', value: 'last-2-weeks' },
  { label: 'Last Month', value: 'last-month' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'Last Year', value: 'last-year' },
  { label: 'All Time', value: 'all-time' },
  { label: 'Custom Range', value: 'custom' },
];

const eventModeOption: ISlidingDateRangeTypeOption = {
  label: 'Event Mode',
  value: 'event',
};

/**
 * @description available sliding date range options to use in date selects
 * @param options
 */
export const slidingDateRangeProvider = (
  options?: IDateRangeConfigOptions
): ISlidingDateRangeTypeOption[] => {
  let result: ISlidingDateRangeTypeOption[];

  if (!isEmpty(options)) {
    result = defaultSlidingOptions.filter(
      (opt: ISlidingDateRangeTypeOption): boolean => {
        /// default allow
        let isAllowed: boolean = true;

        if (options?.disallowCustomRange && isAllowed) {
          isAllowed = opt.value !== 'custom';
        }
        return isAllowed;
      }
    );
  } else {
    result = [...defaultSlidingOptions];
  }

  if (options?.allowEventMode) {
    result.push(eventModeOption);
  }

  return result;
};

export const getSlidingRange = (
  opt: ISlidingDateRangeTypeOption,
  customRange?: IRange
): IRange => {
  let start: dayjs.Dayjs;
  let end: dayjs.Dayjs;
  switch (opt.value) {
    case 'today':
      start = dayjs.utc().startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-week':
      start = dayjs.utc().subtract(1, 'week').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-2-weeks':
      start = dayjs.utc().subtract(2, 'week').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-month':
      start = dayjs.utc().subtract(1, 'month').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-3-months':
      start = dayjs.utc().subtract(3, 'month').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-6-months':
      start = dayjs.utc().subtract(6, 'month').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'last-year':
      start = dayjs.utc().subtract(1, 'year').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'all-time':
      start = dayjs.utc('2018-01-01').startOf('day');
      end = dayjs.utc().endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
    case 'custom':
      return customRange;
    case 'event':
      // Event mode: from now onward (far future end so tests keep streaming in)
      start = dayjs.utc();
      end = dayjs.utc().add(1, 'year').endOf('day');
      return {
        from: { label: start.format('MM/DD'), value: start.valueOf() },
        to: { label: end.format('MM/DD'), value: end.valueOf() },
      };
  }
};
